import { useEffect, useRef } from 'react';
import { ChatAuthService } from '../../services/chatService';
import { chatWebSocket } from '../../services/chatWebSocket';
import { extractWaitingCount } from './helpers';

interface WebSocketManagerProps {
  userId?: string;
  chatAuthReady: boolean;
  onNewMessage: (data: unknown) => void;
  onWaitingCountUpdate: (count: number) => void;
  onConversationStateChange: (conversationId: string, newState: string) => void;
  onWaitingForHuman: () => void;
  onConversationAssumed: () => void;
  onTyping: (conversationId: string) => void;
  selectedConversationId: string | null;
}

/* eslint-disable max-lines-per-function */
/**
 * Hook para gerenciar conexão WebSocket do chat
 */
export const useChatWebSocket = ({
  userId,
  chatAuthReady,
  onNewMessage,
  onWaitingCountUpdate,
  onConversationStateChange,
  onWaitingForHuman,
  onConversationAssumed,
  onTyping,
  selectedConversationId,
}: WebSocketManagerProps) => {
  const wsConnected = useRef(false);

  useEffect(() => {
    if (!userId || !chatAuthReady) return;

    const timer = setTimeout(async () => {
      if (!wsConnected.current) {
        try {
          if (!ChatAuthService.isAuthenticated()) return;

          const socket = chatWebSocket.connect();
          wsConnected.current = true;

          socket.onAny((_eventName: string, ..._args: unknown[]) => {});

          // Mensagens
          chatWebSocket.onNewClientMessage((data) => onNewMessage(data));
          chatWebSocket.onBotMessage((data) => onNewMessage(data));
          chatWebSocket.onAdminMessage((data) => onNewMessage(data));
          chatWebSocket.onMessage((data) => onNewMessage(data));

          // Contadores e estados
          chatWebSocket.onWaitingCountUpdated(({ count, conversationId }) => {
            if (typeof count === 'number') {
              onWaitingCountUpdate(count);
            }
            if (conversationId && selectedConversationId === conversationId) {
              // Recarregar conversa específica se necessário
            }
          });

          chatWebSocket.onWaitingForHuman(() => {
            onWaitingForHuman();
          });

          chatWebSocket.onConversationAssumed(() => onConversationAssumed());

          chatWebSocket.onConversationStateChanged((data) =>
            onConversationStateChange(
              String(data.conversationId || ''),
              String(data.newState || '')
            )
          );

          chatWebSocket.onStateChanged((data) =>
            onConversationStateChange(
              String(data.conversationId || ''),
              String(data.newState || '')
            )
          );

          // Typing indicator
          chatWebSocket.onTyping((data) => {
            if (data.conversationId === selectedConversationId) {
              onTyping(data.conversationId);
            }
          });

          // Eventos alternativos de contagem
          const handleWaitingCount = (data: unknown) => {
            const count = extractWaitingCount(data);
            if (count !== null) onWaitingCountUpdate(count);
          };

          chatWebSocket.addEventListener('waitingCountUpdated', handleWaitingCount);
          chatWebSocket.addEventListener('waiting_count_updated', handleWaitingCount);
          chatWebSocket.addEventListener('updateWaitingCount', handleWaitingCount);
        } catch (err) {
          wsConnected.current = false;
        }
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      if (wsConnected.current) {
        chatWebSocket.disconnect();
        wsConnected.current = false;
        ChatAuthService.clearAuth();
      }
    };
  }, [
    userId,
    chatAuthReady,
    onNewMessage,
    onWaitingCountUpdate,
    onConversationStateChange,
    onWaitingForHuman,
    onConversationAssumed,
    onTyping,
    selectedConversationId,
  ]);

  return {
    isConnected: wsConnected.current,
  };
};
